import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { getCroppedImg, COVER_ASPECT } from '../../utils/cropImage';

// Cover-photo editor for a vehicle gallery image. Lets the owner pan/zoom to
// frame the car, bakes a cropped JPEG, and sets it as the card cover
// (primaryImageUrl). The full original stays in the gallery untouched.
//
// Props:
//   imageUrl   – source gallery image url
//   vehicleId  – userVehicle id
//   imageId    – VehicleImage id being set as cover
//   onClose()  – dismiss without changes
//   onDone(primaryImageUrl, imageId) – cover saved
const ThumbnailCropModal = ({ imageUrl, vehicleId, imageId, onClose, onDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const post = async (file) => {
    const fd = new FormData();
    if (file) fd.append('thumbnail', file);
    const { data } = await api.post(
      `/user-vehicles/${vehicleId}/images/${imageId}/thumbnail`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data.primaryImageUrl;
  };

  const saveCropped = async () => {
    setSaving(true);
    try {
      const file = croppedAreaPixels
        ? await getCroppedImg(imageUrl, croppedAreaPixels, 'cover.jpg')
        : null;
      const primaryImageUrl = await post(file);
      toast.success('Thumbnail updated!');
      onDone(primaryImageUrl, imageId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save thumbnail');
    } finally {
      setSaving(false);
    }
  };

  const saveFull = async () => {
    setSaving(true);
    try {
      const primaryImageUrl = await post(null);
      toast.success('Thumbnail updated!');
      onDone(primaryImageUrl, imageId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save thumbnail');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pe-overlay" onClick={onClose}>
      <div className="pe-modal" onClick={e => e.stopPropagation()}>
        <div className="pe-header">
          <button className="pe-header-btn" onClick={onClose} disabled={saving}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <span className="pe-header-title">Set thumbnail</span>
          <button className="pe-header-btn pe-header-btn--next" onClick={saveCropped} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        <div className="pe-edit">
          <div className="pe-crop-wrap">
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={COVER_ASPECT}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              style={{ containerStyle: { background: '#06080f' } }}
            />
          </div>

          <div className="pe-zoom">
            <span className="pe-zoom-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input
              type="range" min={1} max={3} step={0.01}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              className="pe-zoom-slider"
            />
            <span className="pe-zoom-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
          </div>

          <div style={{ textAlign: 'center', padding: '4px 0 14px' }}>
            <button
              type="button"
              onClick={saveFull}
              disabled={saving}
              style={{ background: 'none', border: 'none', color: 'var(--accent, #5b7fff)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', padding: 6 }}
            >
              Use full photo instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThumbnailCropModal;
